import { BLUE_RAMP, CHART_INK, divergerendeKleur, inktOpRamp, sequentieleKleur } from '../lib/colors';
import { onderling } from '../lib/elo';
import { fmtDatum, fmtDatumKort, initialen, toonRating } from '../lib/format';
import type { Match, Player } from '../lib/types';

/* ==========================================================================
   Grafieken als SVG, met de hand getekend — geen grafiekbibliotheek.

   Vaste regels: dunne markeringen, terughoudend raster, een legenda zodra er
   meer dan één reeks is, en directe labels alleen bij vier reeksen of minder.
   Elke markering draagt een <title>, zodat de cijfers met de muis te lezen
   zijn zonder dat het scherm volstaat met getallen.
   ========================================================================== */

export interface Punt {
  x: number;
  y: number;
}

export interface Reeks {
  naam: string;
  kleur: string;
  punten: Punt[];
  /**
   * Streepjespatroon voor de lijn (SVG strokeDasharray). SERIES houdt op bij
   * acht kleuren; wil je meer reeksen tegelijk tonen, dan onderscheidt het
   * patroon de negende van de eerste. Kleur alleen is dan niet genoeg.
   */
  streep?: string;
}

function netteStap(ruwe: number): number {
  const macht = Math.pow(10, Math.floor(Math.log10(ruwe)));
  const rest = ruwe / macht;
  const stap = rest > 5 ? 10 : rest > 2 ? 5 : rest > 1 ? 2 : 1;
  return stap * macht;
}

/** Een vlak achter de lijn, bijvoorbeeld het bereik van een rang. */
export interface Band {
  van: number;
  tot: number;
  kleur: string;
  label: string;
}

export function LijnGrafiek({
  reeksen,
  hoogte = 240,
  basis,
  banden = []
}: {
  reeksen: Reeks[];
  hoogte?: number;
  basis?: number;
  banden?: Band[];
}) {
  const breedte = 760;
  const directeLabels = reeksen.length <= 4;
  const links = 46;
  const rechts = directeLabels ? 96 : 16;
  const boven = 12;
  const onder = 26;
  const vlakB = breedte - links - rechts;
  const vlakH = hoogte - boven - onder;

  const punten = reeksen.flatMap((r) => r.punten);
  if (punten.length < 2) return null;

  let xMin = Infinity;
  let xMax = -Infinity;
  let yMin = Infinity;
  let yMax = -Infinity;
  for (const p of punten) {
    if (p.x < xMin) xMin = p.x;
    if (p.x > xMax) xMax = p.x;
    if (p.y < yMin) yMin = p.y;
    if (p.y > yMax) yMax = p.y;
  }
  if (basis !== undefined) {
    yMin = Math.min(yMin, basis);
    yMax = Math.max(yMax, basis);
  }
  const speling = Math.max(10, (yMax - yMin) * 0.12);
  yMin -= speling;
  yMax += speling;
  if (xMax === xMin) xMax = xMin + 1;

  const x = (v: number) => links + ((v - xMin) / (xMax - xMin)) * vlakB;
  const y = (v: number) => boven + vlakH - ((v - yMin) / (yMax - yMin)) * vlakH;

  const stap = netteStap((yMax - yMin) / 4);
  const rasterWaarden: number[] = [];
  for (let v = Math.ceil(yMin / stap) * stap; v <= yMax; v += stap) rasterWaarden.push(v);

  const datums = [0, 1, 2, 3].map((i) => xMin + ((xMax - xMin) * i) / 3);

  return (
    <>
      <svg viewBox={`0 0 ${breedte} ${hoogte}`} className="block w-full" role="img" aria-label="Ratingverloop">
        {/* Rangen als vlakken achter de lijn: zo zie je niet alleen dát je
            stijgt, maar ook waar de volgende grens ligt. */}
        {banden.map((b) => {
          const bovenkant = y(Math.min(b.tot, yMax));
          const onderkant = y(Math.max(b.van, yMin));
          if (onderkant - bovenkant < 2) return null;
          return (
            <g key={b.label}>
              <rect
                x={links}
                y={bovenkant}
                width={vlakB}
                height={onderkant - bovenkant}
                fill={b.kleur}
                opacity={0.08}
              />
              <text x={links + vlakB - 6} y={onderkant - 5} textAnchor="end" fontSize={10} fill={b.kleur}>
                {b.label}
              </text>
            </g>
          );
        })}

        {rasterWaarden.map((v) => (
          <g key={v}>
            <line x1={links} y1={y(v)} x2={links + vlakB} y2={y(v)} stroke={CHART_INK.raster} strokeWidth={1} />
            <text x={links - 8} y={y(v) + 4} textAnchor="end" fontSize={11} fill={CHART_INK.label}>
              {Math.round(v)}
            </text>
          </g>
        ))}

        {basis !== undefined ? (
          <>
            <line
              x1={links}
              y1={y(basis)}
              x2={links + vlakB}
              y2={y(basis)}
              stroke={CHART_INK.basislijn}
              strokeDasharray="4 4"
            />
            <text x={links + 4} y={y(basis) - 5} fontSize={11} fill={CHART_INK.label}>
              start {basis}
            </text>
          </>
        ) : null}

        <line
          x1={links}
          y1={boven + vlakH}
          x2={links + vlakB}
          y2={boven + vlakH}
          stroke={CHART_INK.basislijn}
          strokeWidth={1}
        />

        {datums.map((v, i) => (
          <text
            key={i}
            x={x(v)}
            y={hoogte - 6}
            textAnchor={i === 0 ? 'start' : i === 3 ? 'end' : 'middle'}
            fontSize={11}
            fill={CHART_INK.label}
          >
            {fmtDatumKort(new Date(v))}
          </text>
        ))}

        {reeksen.map((s) => {
          const gesorteerd = s.punten.slice().sort((a, b) => a.x - b.x);
          const d = gesorteerd.map((p, n) => `${n ? 'L' : 'M'}${x(p.x).toFixed(1)} ${y(p.y).toFixed(1)}`).join(' ');
          const laatste = gesorteerd[gesorteerd.length - 1];
          return (
            <g key={s.naam}>
              <path
                d={d}
                fill="none"
                stroke={s.kleur}
                strokeWidth={2}
                strokeDasharray={s.streep}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {gesorteerd.length <= 120
                ? gesorteerd.map((p, n) => (
                    <circle key={n} cx={x(p.x)} cy={y(p.y)} r={2.5} fill={s.kleur}>
                      <title>{`${s.naam} — ${fmtDatum(new Date(p.x))}: ${toonRating(p.y)}`}</title>
                    </circle>
                  ))
                : null}
              {directeLabels ? (
                <>
                  <circle cx={x(laatste.x)} cy={y(laatste.y)} r={4} fill={s.kleur} stroke="#fff" strokeWidth={2} />
                  <text x={x(laatste.x) + 9} y={y(laatste.y) + 4} fontSize={11} fontWeight={600} fill={CHART_INK.tekst}>
                    {s.naam}
                  </text>
                </>
              ) : null}
            </g>
          );
        })}
      </svg>

      {reeksen.length > 1 ? (
        <Legenda
          items={reeksen.map((s) => ({
            kleur: s.kleur,
            streep: s.streep,
            label: `${s.naam} — ${toonRating(s.punten[s.punten.length - 1].y)}`
          }))}
        />
      ) : null}
    </>
  );
}

export function Legenda({ items }: { items: { kleur: string; label: string; streep?: string }[] }) {
  return (
    <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-2">
      {items.map((i) => (
        <span key={i.label} className="inline-flex items-center gap-1.5 text-[12.5px] text-krijt-600">
          {/* Een gestreepte reeks krijgt een gestreept vlaggetje; anders zijn
              twee reeksen met dezelfde kleur in de legenda niet te scheiden. */}
          <span
            className={`shrink-0 rounded-sm ${i.streep ? 'h-1 w-4' : 'h-2.5 w-2.5'}`}
            style={
              i.streep
                ? { backgroundImage: `repeating-linear-gradient(90deg, ${i.kleur} 0 4px, transparent 4px 7px)` }
                : { background: i.kleur }
            }
          />
          {i.label}
        </span>
      ))}
    </div>
  );
}

export interface StaafRij {
  label: string;
  waarde: number;
  titel?: string;
}

export function StaafGrafiek({ rijen, hoogte = 190, kleur }: { rijen: StaafRij[]; hoogte?: number; kleur: string }) {
  if (!rijen.length) return null;
  const breedte = 560;
  const links = 34;
  const rechts = 8;
  const boven = 16;
  const onder = 22;
  const vlakB = breedte - links - rechts;
  const vlakH = hoogte - boven - onder;

  const max = Math.max(...rijen.map((r) => r.waarde)) || 1;
  const breed = vlakB / rijen.length;
  const staafB = Math.max(4, breed - 2); /* 2px lucht tussen de staven */
  const hoogsteIndex = rijen.reduce((best, r, i) => (r.waarde > rijen[best].waarde ? i : best), 0);

  return (
    <svg viewBox={`0 0 ${breedte} ${hoogte}`} className="block w-full" role="img" aria-label="Potjes per maand">
      <text x={links - 6} y={boven + 4} textAnchor="end" fontSize={11} fill={CHART_INK.label}>
        {max}
      </text>
      <text x={links - 6} y={boven + vlakH} textAnchor="end" fontSize={11} fill={CHART_INK.label}>
        0
      </text>
      <line
        x1={links}
        y1={boven + vlakH}
        x2={links + vlakB}
        y2={boven + vlakH}
        stroke={CHART_INK.basislijn}
        strokeWidth={1}
      />
      {rijen.map((r, i) => {
        const h = (r.waarde / max) * vlakH;
        const px = links + i * breed + (breed - staafB) / 2;
        const py = boven + vlakH - h;
        const straal = Math.min(4, staafB / 2, h);
        /* afgeronde bovenkant, voet plat op de basislijn */
        const d =
          `M${px} ${boven + vlakH} V${py + straal} Q${px} ${py} ${px + straal} ${py} ` +
          `H${px + staafB - straal} Q${px + staafB} ${py} ${px + staafB} ${py + straal} V${boven + vlakH} Z`;
        return (
          <g key={`${r.label}-${i}`}>
            <path d={d} fill={kleur}>
              <title>{r.titel ?? `${r.label}: ${r.waarde}`}</title>
            </path>
            {i === hoogsteIndex ? (
              <text x={px + staafB / 2} y={py - 5} textAnchor="middle" fontSize={11} fontWeight={600} fill={CHART_INK.tekst}>
                {r.waarde}
              </text>
            ) : null}
            <text x={px + staafB / 2} y={hoogte - 6} textAnchor="middle" fontSize={11} fill={CHART_INK.label}>
              {r.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function Heatmap({
  rijLabels,
  kolLabels,
  waarden
}: {
  rijLabels: string[];
  kolLabels: string[];
  waarden: number[][];
}) {
  const cel = 38;
  const celH = 30;
  const links = 62;
  const boven = 18;
  const tussen = 2;
  const breedte = links + kolLabels.length * cel;
  const hoogte = boven + rijLabels.length * celH + 6;
  const max = Math.max(0, ...waarden.flat());

  return (
    <>
      <svg
        viewBox={`0 0 ${breedte} ${hoogte}`}
        className="block w-full"
        role="img"
        aria-label="Potjes per dagdeel en weekdag"
      >
        {kolLabels.map((l, k) => (
          <text
            key={l}
            x={links + k * cel + (cel - tussen) / 2}
            y={boven - 6}
            textAnchor="middle"
            fontSize={11}
            fill={CHART_INK.label}
          >
            {l}
          </text>
        ))}
        {rijLabels.map((l, r) => (
          <text key={l} x={links - 8} y={boven + r * celH + celH / 2 + 2} textAnchor="end" fontSize={11} fill={CHART_INK.label}>
            {l}
          </text>
        ))}
        {waarden.map((rij, r) =>
          rij.map((v, k) => {
            const px = links + k * cel;
            const py = boven + r * celH;
            const vulling = sequentieleKleur(v, max);
            return (
              <g key={`${r}-${k}`}>
                <rect
                  x={px}
                  y={py}
                  width={cel - tussen}
                  height={celH - tussen}
                  rx={3}
                  fill={vulling ?? '#f3f2ee'}
                >
                  <title>{`${rijLabels[r]}, ${kolLabels[k]}: ${v} potjes`}</title>
                </rect>
                {v ? (
                  <text
                    x={px + (cel - tussen) / 2}
                    y={py + celH / 2 + 2}
                    textAnchor="middle"
                    fontSize={10.5}
                    fill={inktOpRamp(v, max)}
                    className="nums"
                  >
                    {v}
                  </text>
                ) : null}
              </g>
            );
          })
        )}
      </svg>
      <div className="mt-2 flex items-center gap-1.5 text-[11.5px] text-krijt-500">
        0
        <span className="flex">
          {BLUE_RAMP.filter((_, i) => i % 2 === 0).map((c) => (
            <i key={c} className="block h-2.5 w-4" style={{ background: c }} />
          ))}
        </span>
        {max} potjes
      </div>
    </>
  );
}

export function BalansMatrix({ spelers, potjes }: { spelers: Player[]; potjes: Match[] }) {
  const cel = 38;
  const boven = 20;
  const tussen = 2;
  /* Ruimte voor de namen links: ze staan rechts uitgelijnd tegen de matrix, dus
     te weinig ruimte knipt ze aan de voorkant af. 5,8px per teken komt bij
     lettergrootte 11 aardig uit; boven de 160 wordt de kop zelf te smal. */
  const langsteNaam = spelers.reduce((n, p) => Math.max(n, p.name.length), 0);
  const links = Math.max(96, Math.min(160, Math.round(langsteNaam * 5.8) + 14));
  const breedte = links + spelers.length * cel;
  const hoogte = boven + spelers.length * cel + 6;

  /*
   * Bij een volle poule wordt de matrix breder dan de kaart. Indikken maakt de
   * percentages onleesbaar, dus vanaf die breedte houdt hij zijn eigen maat en
   * schuift de kaart horizontaal mee.
   */
  const eigenMaat = breedte > 430;

  return (
    <>
      <div className={eigenMaat ? 'overflow-x-auto' : undefined}>
        <svg
          viewBox={`0 0 ${breedte} ${hoogte}`}
          className="block w-full"
          style={eigenMaat ? { minWidth: breedte } : undefined}
          role="img"
          aria-label="Onderlinge balans tussen spelers"
        >
          {spelers.map((p, k) => (
            <text
              key={p.id}
              x={links + k * cel + (cel - tussen) / 2}
              y={boven - 7}
              textAnchor="middle"
              fontSize={11}
              fill={CHART_INK.label}
            >
              {initialen(p.name)}
            </text>
          ))}
          {spelers.map((p, r) => (
            <text key={p.id} x={links - 8} y={boven + r * cel + cel / 2 + 2} textAnchor="end" fontSize={11} fill={CHART_INK.label}>
              {p.name}
            </text>
          ))}
          {spelers.map((rij, r) =>
            spelers.map((kol, k) => {
              const px = links + k * cel;
              const py = boven + r * cel;
              if (r === k) {
                return <rect key={`${r}-${k}`} x={px} y={py} width={cel - tussen} height={cel - tussen} rx={3} fill="#f3f2ee" />;
              }
              const o = onderling(rij.id, kol.id, potjes);
              if (!o.gespeeld) {
                return (
                  <g key={`${r}-${k}`}>
                    <rect x={px} y={py} width={cel - tussen} height={cel - tussen} rx={3} fill="#f3f2ee" />
                    <text x={px + (cel - tussen) / 2} y={py + cel / 2 + 2} textAnchor="middle" fontSize={10.5} fill={CHART_INK.as}>
                      ·
                    </text>
                  </g>
                );
              }
              const deel = o.gewonnenA / o.gespeeld;
              const t = (deel - 0.5) * 2;
              return (
                <g key={`${r}-${k}`}>
                  <rect x={px} y={py} width={cel - tussen} height={cel - tussen} rx={3} fill={divergerendeKleur(t)}>
                    <title>{`${rij.name} tegen ${kol.name}: ${o.gewonnenA} van ${o.gespeeld} gewonnen`}</title>
                  </rect>
                  <text
                    x={px + (cel - tussen) / 2}
                    y={py + cel / 2 + 2}
                    textAnchor="middle"
                    fontSize={10.5}
                    fill={Math.abs(t) > 0.55 ? '#fff' : CHART_INK.tekst}
                    className="nums"
                  >
                    {Math.round(deel * 100)}
                  </text>
                </g>
              );
            })
          )}
        </svg>
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-[11.5px] text-krijt-500">
        vaker verloren
        <span className="flex">
          {[-1, -0.66, -0.33, 0, 0.33, 0.66, 1].map((t) => (
            <i key={t} className="block h-2.5 w-4" style={{ background: divergerendeKleur(t) }} />
          ))}
        </span>
        vaker gewonnen
      </div>
      <p className="mt-2 text-xs text-krijt-500">
        Het getal is het winstpercentage van de speler in de rij tegen de speler in de kolom.
      </p>
    </>
  );
}
